import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';
import { CategoryChips } from '@/components/leads/CategoryChips';
import { leadCardModel, todayIso, type LeadRecord, type PlatformKind } from '@/data/leads-model';

/* Mobile-first lead card (owner spec 04-10-2026): business, category, area,
 * current booking platform (+ what kind of evidence backs it), sales status,
 * visit approach and next follow-up — plus Website / Call / Email / Maps actions
 * where the data exists (Email = mailto: to the user's own mail app; hidden for
 * suppressed / erased leads). Everything shown comes from leadCardModel(); the
 * component is a thin renderer so the model is unit-tested without a DOM.
 * Actions sit in two equal-width rows so nothing clips at 320–390 px wide.
 * The <article> is `relative`: the screen-reader-only <dt>s are absolutely
 * positioned and must stay INSIDE the card, otherwise they escape the scroll
 * container and inflate the document height (IVG §14 N2). */

const KIND_TONE: Record<PlatformKind, Parameters<typeof Badge>[0]['tone']> = {
  unknown: 'neutral',
  directory_listing: 'warning',
  confirmed_system: 'compliance',
  direct_website: 'success',
  in_house_system: 'success',
};

const STATUS_TONE: Record<string, Parameters<typeof Badge>[0]['tone']> = {
  DISCOVERED: 'neutral',
  QUALIFIED: 'neutral',
  CONTACTED: 'compliance',
  FOLLOWUP_DUE: 'warning',
  REPLIED_INTERESTED: 'success',
  REPLIED_NOT_INTERESTED: 'danger',
  DEMO_BOOKED: 'success',
  SIGNED_UP: 'ai',
  CONVERTED_TENANT: 'ai',
};

export function LeadCard({ lead, today = todayIso(), onQuickUpdate }: { lead: LeadRecord; today?: string; onQuickUpdate?: (lead: LeadRecord) => void }) {
  const m = leadCardModel(lead, today);
  return (
    <article className="relative rounded-lg border border-border-subtle bg-surface p-3 shadow-sm" data-testid="lead-card" data-ref={m.ref}>
      <div className="flex items-start justify-between gap-2">
        <Link to={`/sales/leads/${encodeURIComponent(m.ref)}`} className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-semibold leading-tight text-text-primary">{m.title}</h3>
          <p className="mt-0.5 truncate text-xs text-text-secondary">
            {[m.category, m.area].filter(Boolean).join(' · ') || '—'}
            {m.postcode ? <span className="text-text-tertiary"> · {m.postcode}</span> : null}
          </p>
          <CategoryChips names={m.categories} className="mt-1.5" />
        </Link>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {m.priority ? <Badge tone={m.priority === 'A' ? 'danger' : m.priority === 'B' ? 'warning' : 'neutral'}>P{m.priority}</Badge> : null}
          <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>{m.statusLabel}</Badge>
        </div>
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <div className="col-span-2">
          <dt className="sr-only">Booking platform</dt>
          <dd className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-text-primary">{m.platform}</span>
            <Badge tone={KIND_TONE[m.platformKind]}>{m.platformKindLabel}</Badge>
          </dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wide text-text-tertiary">Approach</dt>
          <dd className="text-text-primary">{m.approachLabel ?? <span className="text-text-tertiary">Not set</span>}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wide text-text-tertiary">Next follow-up</dt>
          <dd className={cn('text-text-primary', m.followUpOverdue && 'font-semibold text-danger')}>
            {m.followUp ?? <span className="text-text-tertiary">None</span>}
            {m.followUpOverdue ? ' · overdue' : ''}
          </dd>
        </div>
        {m.email ? (
          <div className="col-span-2 min-w-0">
            <dt className="sr-only">Email</dt>
            <dd className="truncate select-all text-text-secondary" title={m.email}>{m.email}</dd>
          </div>
        ) : null}
        {m.routeDay ? (
          <div className="col-span-2">
            <dt className="sr-only">Route day</dt>
            <dd className="text-text-tertiary">{m.routeDay}</dd>
          </div>
        ) : null}
      </dl>

      <div className="mt-3 grid grid-cols-4 gap-2" data-testid="lead-card-actions">
        <ActionLink href={m.actions.website} label="Website" />
        <ActionLink href={m.actions.tel} label="Call" />
        <ActionLink href={m.actions.mail} label="Email" />
        <ActionLink href={m.actions.maps} label="Maps" />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {onQuickUpdate ? (
          <button
            type="button"
            onClick={() => onQuickUpdate(lead)}
            className="flex h-10 items-center justify-center rounded-md border border-border-default bg-surface px-2 text-xs font-medium text-text-primary hover:bg-hover"
          >
            Update
          </button>
        ) : null}
        <Link
          to={`/sales/leads/${encodeURIComponent(m.ref)}`}
          className={cn('flex h-10 items-center justify-center rounded-md bg-neutral-10 px-2 text-xs font-medium text-neutral-0 hover:bg-neutral-9 dark:bg-neutral-11', !onQuickUpdate && 'col-span-2')}
        >
          Details
        </Link>
      </div>
    </article>
  );
}

const ACTION_BASE = 'flex h-10 min-w-0 items-center justify-center truncate rounded-md px-1 text-xs';

function ActionLink({ href, label }: { href: string | null; label: string }) {
  if (!href) {
    return (
      <span className={cn(ACTION_BASE, 'border border-dashed border-border-subtle text-text-tertiary')} aria-disabled="true" title={`No ${label.toLowerCase()} on file`}>
        {label}
      </span>
    );
  }
  const external = !/^(tel|mailto):/i.test(href);
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={cn(ACTION_BASE, 'border border-border-default bg-surface font-medium text-text-primary hover:bg-hover')}
    >
      {label}
    </a>
  );
}
